<template>
  <div class="fun-root">
    <transition name="fun-fade">
      <div v-if="visible" class="fun-banner" role="status" aria-live="polite">
        <i class="fa fa-refresh fun-icon" aria-hidden="true"></i>
        <span class="fun-text">A new version of Fleet Integrations is ready. It will load the next time you open a page.</span>
        <span class="fun-actions">
          <button type="button" class="fun-btn fun-btn-primary" @click="refreshNow">Refresh now</button>
          <button type="button" class="fun-btn" @click="later">Later</button>
        </span>
      </div>
    </transition>
    <b-modal id="fi-update-nudge-dirty" title="Unsaved changes" ok-only ok-title="OK" centered>
      You have unsaved changes on this page. Save them first, then refresh.
    </b-modal>
  </div>
</template>

<script>
import { state, nudgeDue, isAnyDirty, reloadInto, CURRENT_BUILD } from '../utils/build_watcher'
import { isChromelessPath } from '../utils/chromeless_routes'
import { fsEvent } from '../utils/fullstory'

// how often the banner re-evaluates nudgeDue; the watcher state itself is
// observable, so a new build shows up without waiting for this
const NUDGE_TICK_MS = 30 * 1000

// FI-2181: a calm site-wide strip, rendered right after ServiceAlertBanner
// at both authenticated mount points in App.vue. self-gates on the build
// watcher state (utils/build_watcher.js): nothing renders until a newer
// build has been live for NUDGE_AFTER_MS on the same page. never on
// chromeless or login routes. no showAlert, no toast, no service banner.
export default {
  name: 'FiUpdateNudge',
  data() {
    return {
      watcher: state,
      nowMs: Date.now(),
    }
  },
  computed: {
    visible() {
      if (!CURRENT_BUILD) return false
      const route = this.$route || {}
      if (route.name === 'login' || route.name === 'login_redirect') return false
      const hashPath = String((typeof window !== 'undefined' && window.location.hash) || '').replace(/^#/, '')
      if (isChromelessPath(route.fullPath, hashPath, route.path)) return false
      return nudgeDue({
        newBuildSeenAt: this.watcher.newBuildSeenAt,
        nowMs: this.nowMs,
        dismissedForBuild: this.watcher.dismissedForBuild,
        liveBuild: this.watcher.liveBuild,
      })
    },
  },
  mounted() {
    this._nudgeTimer = setInterval(() => { this.nowMs = Date.now() }, NUDGE_TICK_MS)
  },
  beforeDestroy() {
    clearInterval(this._nudgeTimer)
  },
  methods: {
    refreshNow() {
      if (isAnyDirty()) {
        this.$bvModal.show('fi-update-nudge-dirty')
        return
      }
      fsEvent('build_refresh', { mode: 'nudge' })
      reloadInto(this.$route.fullPath)
    },
    // per build: a newer build brings the banner back
    later() {
      this.watcher.dismissedForBuild = this.watcher.liveBuild
    },
  },
}
</script>

<style scoped>
/* info tone, a notch calmer than the amber service-alert strip it sits
   under (service_alert_banner.vue) */
.fun-banner {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 8px 16px;
  background: linear-gradient(180deg, #f1f6fb 0%, #e6eff8 100%);
  border-bottom: 1px solid #c3d6e8;
  color: #23425e;
  font-size: 13px;
  text-align: left;
}

.fun-icon {
  flex: 0 0 auto;
  color: #4682b4;
  font-size: 15px;
}

.fun-text {
  flex: 1 1 auto;
  min-width: 0;
  line-height: 1.3;
}

.fun-actions {
  flex: 0 0 auto;
  display: inline-flex;
  gap: 6px;
}

.fun-btn {
  background: rgba(255, 255, 255, 0.7);
  border: 1px solid #b9cfe3;
  color: #23425e;
  padding: 4px 10px;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.12s;
}
.fun-btn:hover { background: #fff; }
.fun-btn:focus { outline: none; }
.fun-btn:focus-visible { outline: 2px solid #4682b4; outline-offset: 2px; }

.fun-btn-primary {
  background: #4682b4;
  border-color: #4682b4;
  color: #fff;
}
.fun-btn-primary:hover { background: #3d73a0; }

.fun-fade-enter-active, .fun-fade-leave-active { transition: opacity 0.2s, transform 0.2s; }
.fun-fade-enter, .fun-fade-leave-to { opacity: 0; transform: translateY(-4px); }

/* phones: text on top, actions below it */
@media (max-width: 640px) {
  .fun-banner { flex-wrap: wrap; padding: 8px 12px; gap: 8px; }
  .fun-text { flex-basis: calc(100% - 27px); }
  .fun-actions { margin-left: 27px; }
}
</style>
